import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Dimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useToast } from "../../components/LhToastContainer";
import useTicketingAccess from "./hooks/useTicketingAccess";
import useTicketingConfig from "./hooks/useTicketingConfig";
import LHLoader from "../../components/LHLoader";
import TeamAssignment from "./TeamAssignment";
import CategoryMapping from "./CategoryMapping";
import styles from "./TicketingConfigurationsScreen.styles";

type Props = {
  onNavigate?: (screen: string) => void;
};

type TabType = "team" | "category";

const TicketingConfigurationsScreen: React.FC<Props> = ({ onNavigate }) => {
  const { isSustainTeamMember, hasWriteAccess, accessLoading } = useTicketingAccess();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<TabType>("team");
  const [loading, setLoading] = useState(false);
  const windowWidth = Dimensions.get("window").width;

  const {
    teams,
    teamsLoading,
    assignmentRows,
    assignmentsLoading,
    categories,
    subcategories,
    tertiaries,
    categoriesLoading,
    subcategoriesLoading,
    loadSubcategoriesForCategory,
    loadTertiariesForSubcategory,
    isSustainTeamLead,
    currentUserTeamId,
    sustainRoleLoading,
    error: configError,
    refetchCategories,
    refetchAssignments,
    saveTeams,
    refetchTeams,
    updateAssignment,
    addCategory,
    addSubcategory,
    addTertiary,
    editCategory,
    editSubcategory,
    editTertiary,
    toggleCategory,
    toggleSubcategory,
    toggleTertiary,
  } = useTicketingConfig();

  // Leads can edit any team's mapping/assignment.
  // Members can edit only the rows/categories that belong to their own team.
  const canEditConfig = hasWriteAccess && isSustainTeamLead;
  const isMemberReadOnly = hasWriteAccess && !isSustainTeamLead && !sustainRoleLoading;

  // For a Member: resolve the TeamEntry key for the current user's team so
  // TeamAssignment can restrict editing to that team's rows.
  const memberTeamKey = useMemo(() => {
    if (!isMemberReadOnly || currentUserTeamId === null) return undefined;
    const entry = teams.find((t) => Number(t.key) === currentUserTeamId);
    return entry?.key;
  }, [isMemberReadOnly, currentUserTeamId, teams]);

  // For a Member: derive the sets of category / subcategory / tertiary IDs that
  // are linked to their team via the team assignment rows, so CategoryMapping
  // can restrict editing to only those items.
  const memberCategoryIds = useMemo<Set<string> | undefined>(() => {
    if (!isMemberReadOnly || memberTeamKey === undefined) return undefined;
    const ids = new Set<string>();
    assignmentRows.forEach((row) => {
      const entry = teams.find((t) => t.name === row.team);
      if (entry && entry.key === memberTeamKey) {
        const cat = categories.find((c) => c.name === row.category);
        if (cat) ids.add(cat.id);
      }
    });
    return ids;
  }, [isMemberReadOnly, memberTeamKey, assignmentRows, teams, categories]);

  const memberSubcategoryIds = useMemo<Set<string> | undefined>(() => {
    if (!isMemberReadOnly || memberTeamKey === undefined) return undefined;
    const ids = new Set<string>();
    assignmentRows.forEach((row) => {
      const entry = teams.find((t) => t.name === row.team);
      if (entry && entry.key === memberTeamKey) {
        const sub = subcategories.find(
          (s) => s.name === row.subcategory && categories.find((c) => c.name === row.category && c.id === s.categoryId)
        );
        if (sub) ids.add(sub.id);
      }
    });
    return ids;
  }, [isMemberReadOnly, memberTeamKey, assignmentRows, teams, categories, subcategories]);

  const memberTertiaryIds = useMemo<Set<string> | undefined>(() => {
    if (!isMemberReadOnly || memberTeamKey === undefined) return undefined;
    const ids = new Set<string>();
    assignmentRows.forEach((row) => {
      if (!row.tertiary) return;
      const entry = teams.find((t) => t.name === row.team);
      if (entry && entry.key === memberTeamKey) {
        const sub = subcategories.find(
          (s) => s.name === row.subcategory && categories.find((c) => c.name === row.category && c.id === s.categoryId)
        );
        if (sub) {
          const ter = tertiaries.find((t) => t.name === row.tertiary && t.subcategoryId === sub.id);
          if (ter) ids.add(ter.id);
        }
      }
    });
    return ids;
  }, [isMemberReadOnly, memberTeamKey, assignmentRows, teams, categories, subcategories, tertiaries]);

  useEffect(() => {
    if (configError) toast.show(configError, "error", "Ticketing Configurations");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configError]);
  const isMobile = windowWidth < 768;

  // Route guard – redirect non-sustain users back to the portal landing
  useEffect(() => {
    if (!accessLoading && !isSustainTeamMember) {
      onNavigate?.("Ticketing Portal");
    }
  }, [accessLoading, isSustainTeamMember, onNavigate]);

  const userInfo = useSelector((state: RootState) => state.appData?.userInfo);
  const userRole = useMemo(() => {
    if (userInfo?.sustainTeamMember) return "Sustain Team";
    return userInfo?.roleName || "Field User";
  }, [userInfo]);

  if (accessLoading || loading || sustainRoleLoading) {
    return <LHLoader />;
  }

  if (!isSustainTeamMember) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>
          You do not have permission to access Ticketing Configurations.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.screenRoot}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.breadcrumb}>
            <Pressable onPress={() => onNavigate?.("Ticketing Portal")}>
              <Text style={styles.breadcrumbLink}>Ticketing Portal</Text>
            </Pressable>
            <Text style={styles.breadcrumbSep}>/</Text>
            <Text style={styles.breadcrumbCurrent}>Ticketing Configurations</Text>
          </View>
          <Text style={styles.pageTitle}>Ticketing Configurations</Text>
          <Text style={styles.pageSubtitle}>
            Manage default team routing and the category / subcategory taxonomy
            used across the portal.
          </Text>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        <Pressable
          style={[
            styles.tabButton,
            activeTab === "team" && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab("team")}
        >
          <Text
            style={[
              styles.tabButtonText,
              activeTab === "team" && styles.tabButtonTextActive,
            ]}
          >
            Team Assignment
          </Text>
        </Pressable>
        <Pressable
          style={[
            styles.tabButton,
            activeTab === "category" && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab("category")}
        >
          <Text
            style={[
              styles.tabButtonText,
              activeTab === "category" && styles.tabButtonTextActive,
            ]}
          >
            Category Mapping
          </Text>
        </Pressable>
      </View>

      {/* Tab Content */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {isMemberReadOnly && (
          <View style={styles.readOnlyBanner}>
            <MaterialIcons name="info" size={20} color="#FF8C09" />
            <Text style={styles.readOnlyBannerText}>
              You have member-level access. You can edit team assignments and
              category configurations only for your assigned team.
            </Text>
          </View>
        )}

        {activeTab === "team" && (
          <View style={styles.tabPane}>
            <View style={styles.configTip}>
              <MaterialIcons name="info" size={15} color="#005CBC" />
              <Text style={styles.configTipText}>
                When a ticket is submitted, its category, subcategory, and
                tertiary category determine which Sustain team it routes to.
                Select a row to edit Organization and Team Assignment. SNOW AG
                is read-only and populated by the ServiceNow integration.
                Existing tickets keep their current assignment unless reassigned
                individually.
              </Text>
            </View>
            <TeamAssignment
              rows={assignmentRows}
              readOnly={!canEditConfig && !isMemberReadOnly}
              loading={assignmentsLoading || teamsLoading}
              teams={teams}
              onRefreshTeams={refetchTeams}
              onSaveTeams={canEditConfig ? saveTeams : undefined}
              onToggleStatus={
                canEditConfig || isMemberReadOnly
                  ? (row, newStatus) => updateAssignment(row.id, { active: newStatus === "active" })
                  : undefined
              }
              onEditAssignment={
                canEditConfig || isMemberReadOnly
                  ? (row, updates) => updateAssignment(row.id, updates)
                  : undefined
              }
              memberTeamKey={memberTeamKey}
            />
          </View>
        )}

        {activeTab === "category" && (
          <View style={styles.tabPane}>
            <View style={styles.configTip}>
              <MaterialIcons name="info" size={15} color="#005CBC" />
              <Text style={styles.configTipText}>
                Build the taxonomy used on the Submit Ticket form. Select a
                category, then a subcategory, then optional tertiary values.
                Additional fields can be attached at the subcategory or tertiary
                level.
              </Text>
            </View>
            <CategoryMapping
              categories={categories}
              subcategories={subcategories}
              tertiaries={tertiaries}
              loading={categoriesLoading}
              subcategoriesLoading={subcategoriesLoading}
              onSelectCategory={loadSubcategoriesForCategory}
              onSelectSubcategory={loadTertiariesForSubcategory}
              onAddCategory={canEditConfig ? addCategory : undefined}
              onCategoryCreated={() => Promise.all([refetchCategories(), refetchAssignments()]).then(() => undefined)}
              teams={teams.filter((team) => team.active).map((team) => ({ id: Number(team.key), name: team.name }))}
              onAddSubcategory={canEditConfig || isMemberReadOnly ? addSubcategory : undefined}
              onAddTertiary={canEditConfig || isMemberReadOnly ? addTertiary : undefined}
              onEditCategory={canEditConfig || isMemberReadOnly ? editCategory : undefined}
              onEditSubcategory={canEditConfig || isMemberReadOnly ? editSubcategory : undefined}
              onEditTertiary={canEditConfig || isMemberReadOnly ? editTertiary : undefined}
              onToggleCategory={canEditConfig || isMemberReadOnly ? toggleCategory : undefined}
              onToggleSubcategory={canEditConfig || isMemberReadOnly ? toggleSubcategory : undefined}
              onToggleTertiary={canEditConfig || isMemberReadOnly ? toggleTertiary : undefined}
              editableCategoryIds={memberCategoryIds}
              editableSubcategoryIds={memberSubcategoryIds}
              editableTertiaryIds={memberTertiaryIds}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default TicketingConfigurationsScreen;
